import { IdentityField, TextFieldAdapter } from 'components/enhanced';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { SectionHeader } from 'components/ui/typography';
import { orderService } from 'features/inventory/purchase-orders/api/order.service';
import { supplierService } from 'features/inventory/suppliers/api/SupplierService';
import { ITransportActiveDocument } from 'features/inventory/transport-documents/types';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';

interface TransportActiveDocumenFormParams {
  transport_document: ITransportActiveDocument;
  onSubmit: any;
  isUpdate: boolean;
  isSupplierDdt?: boolean;
  disableEdit?: boolean;
}

export const TransportActiveDocumentForm = (params: TransportActiveDocumenFormParams) => {
  const { transport_document, onSubmit, isUpdate, disableEdit, isSupplierDdt } = params;
  const { t } = useTranslation();

  const [orders, setOrders] = useState<Option[]>([]);
  const [warehouses, setWarehouses] = useState<Option[]>([]);
  const [suppliers, setSuppliers] = useState<Option[]>([]);
  const [selectedWarehouseOut, setSelectedWarehouseOut] = useState<string | null>(
    String(transport_document?.warehouse_out ?? '')
  );

  // --------------------------------------------- GETTERS AND USE EFFECTS

  /**
   * useEffect per il caricamento iniziale dei dati di configurazione globale
   * Gestisce sia il caricamento dei magazzini che dei fornitori in base al contesto operativo.
   */
  useEffect(() => {
    warehouseService.getAllBaseInformation().then(setWarehouses);
    if (isSupplierDdt) {
      supplierService.getAllBaseInformation().then(setSuppliers);
    }
  }, []);

  /**
   * useEffect per il caricamento automatico degli ordini quando viene selezionato un magazzino
   */
  useEffect(() => {
    if (selectedWarehouseOut) {
      fetchOrders();
    }
  }, [selectedWarehouseOut]);

  /**
   * Funzione asincrona che recupera gli ordini disponibili per la creazione di documenti di trasporto.
   * Gestisce sia il caricamento standard che il mantenimento dell'ordine selezionato anche se
   * non presente nella lista filtrata.
   */
  const fetchOrders = () => {
    const params = {
      // 2025-02-26: Introduzione dei Trasferimenti tra magazzini ORA OGNI ORDINE PUò ESSERE SCELTO come valido
      //              nascondo la condizione di visibilità per "codice magazzino"
      //warehouse: selectedWarehouseOut['code'] ?? '',
      status: 1
    };

    orderService.openOrdersNotInDDTactive(1000, 0, params).then((res) => {
      const fetchedOrders = res.results.map((value) => ({
        id: value.id,
        code: value.ref,
        description: value.description
      }));

      // Se esiste un ordine selezionato ma non è nella lista, aggiungilo
      const existingOrderId = transport_document.order;
      const alreadyIncluded = fetchedOrders.some((o) => o.id === existingOrderId);

      if (!alreadyIncluded && existingOrderId) {
        orderService.get(existingOrderId).then((order) => {
          const existingOrder = {
            id: order.id,
            code: order.ref,
            description: order.ref + ' - ' + order.supplier
          };
          setOrders([...fetchedOrders, existingOrder]);
        });
      } else {
        setOrders(fetchedOrders);
      }
    });
  };

  return (
    <>
      {transport_document && orders && (
        <EnhancedForm
          onSubmit={disableEdit ? {} : onSubmit}
          edit={!!transport_document.id}
          hideBackResetButton={true}
          disableEdit={disableEdit}
          formId="amountForm"
          autoStyleLayout={true}
          columns={12}>
          <SectionHeader title={'Campi Principali'} gridSpan />
          <IdentityField
            gridSpan={4}
            name="warehouse_out"
            label={t(`global.warehouse_out`)}
            initialValue={transport_document.warehouse_out}
            initValue={warehouses.find((s) => s.id === transport_document.warehouse_out)}
            component={AutocompleteAdapter}
            options={warehouses}
            required
            disabled={disableEdit || !!transport_document.warehouse_out}
            type="text"
            // this should controll the fetch of orders
            setValue={setSelectedWarehouseOut}
            service={warehouseService}
            submitObject
          />
          {isSupplierDdt ? (
            <IdentityField
              gridSpan={4}
              name="supplier"
              label={t(`global.supplier`)}
              initialValue={transport_document.supplier}
              initValue={suppliers.find((s) => s.id === transport_document.supplier)}
              component={AutocompleteAdapter}
              options={suppliers}
              optionLabel="company_name"
              required
              disabled={disableEdit || !!transport_document.supplier}
              type="text"
            />
          ) : (
            <IdentityField
              gridSpan={4}
              name="warehouse_in"
              label={t(`global.warehouse_in`)}
              initialValue={transport_document.warehouse_in}
              initValue={warehouses.find((s) => s.id === transport_document.warehouse_in)}
              component={AutocompleteAdapter}
              options={warehouses}
              required
              disabled={disableEdit || !!transport_document.warehouse_in}
              type="text"
            />
          )}
          <IdentityField
            gridSpan={4}
            name="order"
            label={t(`global.order`)}
            initialValue={transport_document.order}
            initValue={orders.find((o) => o.id === transport_document.order)}
            component={AutocompleteAdapter}
            options={orders}
            optionLabel="description"
            optionValue="id"
            disabled={
              disableEdit ||
              !!transport_document.order ||
              ((transport_document?.status === 'SENT' ||
                transport_document?.status === 'ARRIVED') &&
                isUpdate)
            }
            autoSelect={false}
          />
          <SectionHeader title={'Documentazione e Data'} gridSpan={true} />
          <IdentityField
            gridSpan={6}
            name="ref"
            label={isSupplierDdt ? t(`pages.supplier-active-ddt.ref`) : t(`pages.active-ddt.ref`)}
            initialValue={transport_document?.ref ?? ''}
            disabled={
              disableEdit ||
              ((transport_document?.status === 'SENT' ||
                transport_document?.status === 'ARRIVED') &&
                isUpdate)
            }
            component={TextFieldAdapter}
            type="text"
          />
          <IdentityField
            gridSpan={6}
            name="date"
            label={isSupplierDdt ? t(`pages.supplier-active-ddt.date`) : t(`pages.active-ddt.date`)}
            component={DatePickerAdapter}
            type="date"
            disabled={
              disableEdit ||
              ((transport_document?.status === 'SENT' ||
                transport_document?.status === 'ARRIVED') &&
                isUpdate)
            }
            initialValue={transport_document?.date ?? null}
            noFuture={true}
          />
          <SectionHeader title={'Informazioni Logistiche'} gridSpan />
          <IdentityField
            gridSpan={6}
            name="colli"
            label={t(`pages.active-ddt.colli`)}
            initialValue={transport_document?.colli ?? ''}
            disabled={
              disableEdit ||
              ((transport_document?.status === 'SENT' ||
                transport_document?.status === 'ARRIVED') &&
                isUpdate)
            }
            component={TextFieldAdapter}
            type="text"
            tooltip="Inserisci il numero complessivo di pacchi che compongono la spedizione."
          />
          <IdentityField
            gridSpan={6}
            name="weight"
            label={t(`pages.active-ddt.weight`)}
            initialValue={transport_document?.weight ?? ''}
            disabled={
              disableEdit ||
              ((transport_document?.status === 'SENT' ||
                transport_document?.status === 'ARRIVED') &&
                isUpdate)
            }
            component={TextFieldAdapter}
            type="text"
            tooltip="Il peso complessivo della merce spedita (solitamente espresso in kg)"
          />
          <IdentityField
            gridSpan={12}
            name="notes"
            label={t(`global.notes`)}
            initialValue={transport_document?.notes ?? ''}
            disabled={
              disableEdit ||
              ((transport_document?.status === 'SENT' ||
                transport_document?.status === 'ARRIVED') &&
                isUpdate)
            }
            component={TextFieldAdapter}
            type="text"
          />
        </EnhancedForm>
      )}
    </>
  );
};

export default TransportActiveDocumentForm;
